import { bind } from "@react-rxjs/core"
import { createSignal } from "@react-rxjs/utils"
import { t } from "i18next"
import {
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  of,
  retry,
  scan,
  shareReplay,
  startWith,
  switchMap,
  takeWhile,
  tap,
} from "rxjs"

import { isValidEmail } from "@/App/Equities/shared/utils"
import {
  AuthService,
  ERROR_LOGIN_RESPONSE,
  type ErrorLoginResponse,
  LoginError,
  type LoginRequest,
  type LoginResponse,
  SUCCESS_LOGIN_RESPONSE,
  type SuccessLoginResponse,
} from "@/generated/TradingGateway"
import { ConnectionStatus, connectionStatus$ } from "@/services/connection"

import { clearEquitiesError, publishEquitiesError } from "../errorAlert"
import { loginErrorKey } from "./loginErrorKey"

const publishLoginError = (message: string) => {
  publishEquitiesError({ source: "login", message: t(message) })
}

// Generated API does not include a "pending" state, so we create one here
// to allow downstream consumers to react to an in-flight login request
const PENDING_LOGIN_RESPONSE = "pending"
type PendingLoginResponse = { type: typeof PENDING_LOGIN_RESPONSE }
const PENDING_LOGIN: PendingLoginResponse = { type: PENDING_LOGIN_RESPONSE }

export const [loginTrigger$, triggerLogin] = createSignal<string>()

const hydraLogIn$ = (username: string) =>
  defer(() =>
    AuthService.login({
      username,
    } satisfies LoginRequest),
  ).pipe(
    tap({
      error: (err) => {
        // the raw failure stays in the console; users get generic translated copy
        console.error("[login] Hydra stream error:", err)
        publishLoginError(loginErrorKey(LoginError.InternalError))
      },
    }),
    retry({ count: 2, delay: 3_000 }),
    catchError(() =>
      of({
        type: ERROR_LOGIN_RESPONSE,
        payload: LoginError.InternalError,
      } as ErrorLoginResponse),
    ),
  )

const loginWhenConnected$ = (username: string) =>
  connectionStatus$.pipe(
    distinctUntilChanged(),
    filter((status) => status === ConnectionStatus.CONNECTED),
    switchMap(() => hydraLogIn$(username)),
    takeWhile(
      (response: LoginResponse) =>
        !(
          response.type === ERROR_LOGIN_RESPONSE &&
          response.payload === LoginError.InvalidCredentials
        ),
      true,
    ),
  )

// reads localStorage at module eval time
const autoLogin$ = import.meta.env.VITE_EQUITIES_MOCKS
  ? EMPTY
  : of(localStorage.getItem("email")).pipe(
      filter((email): email is string => !!email && isValidEmail(email)),
    )

const loginRequests$ = merge(loginTrigger$, autoLogin$)

const logIn$ = loginRequests$.pipe(
  switchMap((username) =>
    loginWhenConnected$(username).pipe(startWith(PENDING_LOGIN)),
  ),
  tap((response) => {
    if (response.type === ERROR_LOGIN_RESPONSE) {
      // Stream failures publish from catchError with the specific error;
      // InternalError here means the synthetic came from catchError, skip.
      if (response.payload !== LoginError.InternalError) {
        console.error("[login] Server-returned error:", response.payload)
        publishLoginError(loginErrorKey(response.payload))
      }
    } else if (response.type === SUCCESS_LOGIN_RESPONSE) {
      localStorage.setItem("email", response.payload.username)
      clearEquitiesError("login")
    }
  }),
  shareReplay(1),
)

export const [useIsLoggingIn, isLoggingIn$] = bind(
  logIn$.pipe(map((response) => response.type === PENDING_LOGIN_RESPONSE)),
  false,
)

export const [useLoggedInUser, loggedInUser$] = bind(
  logIn$.pipe(
    filter(
      (response): response is SuccessLoginResponse =>
        response.type === SUCCESS_LOGIN_RESPONSE,
    ),
    map((response) => response.payload.username),
  ),
  null,
)

export const [useLoginSuccessCount, loginSuccessCount$] = bind(
  logIn$.pipe(
    filter(
      (response): response is SuccessLoginResponse =>
        response.type === SUCCESS_LOGIN_RESPONSE,
    ),
    scan((count) => count + 1, 0),
  ),
  0,
)

// useAuthError is decoupled from the alerter as it drives auth-flow business logic
// (e.g.: login form error state; navigation). It emits the raw error so the
// presentation layer owns the copy.
export const [useAuthError, authError$] = bind(
  logIn$.pipe(
    map((response) =>
      response.type === ERROR_LOGIN_RESPONSE ? response.payload : null,
    ),
  ),
  null,
)
